import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { Store, select, ActionsSubject } from "@ngrx/store";
import { ofType } from "@ngrx/effects";
import { forkJoin, of, race, Subject } from "rxjs";
import { catchError, filter, map, take, takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { MatCheckbox } from "@angular/material/checkbox";

import { ToastrTranslatedService } from "../shared/toastr-translated.service";
import {
  GetIntegrationSettingsAction,
  ChangeIntegrationSettingsAction,
  CHANGE_INTEGRATION_SETTINGS_SUCCESS,
  CHANGE_INTEGRATION_SETTINGS_FAILURE,
  GET_INTEGRATION_SETTINGS_FAILURE,
} from "../settings/settings.actions";
import { getSettingsSelector } from "../settings/settings.reducer";
import {
  IntegrationSettings,
  BillingSettings,
} from "../settings/settings.interface";
import { BillingSettingsService } from "../billing/settings/billing-settings.service";
import { ConfirmationDialogComponent } from "../shared/confirmation-dialog/confirmation-dialog.component";
import { AuthService } from "../auth/auth.service";
import { LimitPlanDialogComponent } from "../shared/limit-plan-dialog/limit-plan-dialog.component";
import { isFeatureBlocked } from "../shared/utils/plan-restriction.util";

@Component({
  selector: "app-extension",
  templateUrl: "./extension.component.html",
  styleUrls: ["./extension.component.scss"],
  standalone: false,
})
export class ExtensionComponent implements OnInit, OnDestroy {
  @ViewChild("internalSignatureCheckbox")
  internalSignatureCheckbox!: MatCheckbox;
  @ViewChild("domainRulesCheckbox") domainRulesCheckbox!: MatCheckbox;

  public extensionId = "hgemmbnchnegomnmpngehokoaendhpoe";
  public outlookMarketplaceUrl =
    "https://marketplace.microsoft.com/en-us/product/office/WA200009959";
  public internalSignatureEnabled = false;
  public domainRulesEnabled = false;
  public lockSignatureEditingEnabled = false;
  public saving = false;
  public checkboxChanged = false;
  public loading = true;
  private currentSettings: IntegrationSettings | null = null;
  private billingSettings?: BillingSettings;
  private destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<unknown>,
    private readonly actions$: ActionsSubject,
    private readonly billingSettingsService: BillingSettingsService,
    private readonly toastr: ToastrTranslatedService,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
    private readonly auth: AuthService,
  ) {
    this.actions$
      .pipe(
        ofType(
          CHANGE_INTEGRATION_SETTINGS_SUCCESS,
          CHANGE_INTEGRATION_SETTINGS_FAILURE,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.saving = false;
      });
  }

  ngOnInit(): void {
    this.loading = true;
    this.store.dispatch(new GetIntegrationSettingsAction());

    forkJoin({
      settings: race(
        this.store.pipe(
          select(getSettingsSelector),
          filter((settings): settings is IntegrationSettings => !!settings),
          take(1),
        ),
        this.actions$.pipe(
          ofType(GET_INTEGRATION_SETTINGS_FAILURE),
          take(1),
          map((): IntegrationSettings | null => null),
        ),
      ),
      billing: this.billingSettingsService
        .get()
        .pipe(catchError(() => of<BillingSettings | null>(null))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ settings, billing }) => {
          if (settings) {
            this.currentSettings = settings;
            this.internalSignatureEnabled = settings.internalSignature === true;
            this.domainRulesEnabled = settings.domainRulesEnabled === true;
            this.lockSignatureEditingEnabled =
              settings.lockSignatureEditing === true;
          }
          this.billingSettings = billing ?? undefined;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  copyToClipboard(text: string): void {
    navigator.clipboard
      .writeText(text)
      .then(() =>
        this.toastr.success(
          this.translate.instant("extension.copied") as string,
        ),
      )
      .catch(() =>
        this.toastr.error(
          this.translate.instant("extension.copyError") as string,
        ),
      );
  }

  toggleInternalSignature(checked: boolean): void {
    if (
      checked &&
      isFeatureBlocked(this.auth.isSuperAdmin, this.billingSettings)
    ) {
      this.internalSignatureCheckbox.checked = false;
      this.internalSignatureEnabled = false;
      const planName = this.billingSettings?.planName?.toLowerCase() || "basic";
      this.dialog.open(LimitPlanDialogComponent, {
        width: "500px",
        maxWidth: "90vw",
        data: {
          plan: planName as "free" | "freemium" | "basic",
          type: "internalSignature",
        },
      });
      return;
    }
    this.internalSignatureEnabled = checked;
    this.checkboxChanged = true;
  }

  toggleDomainRules(checked: boolean): void {
    if (
      checked &&
      isFeatureBlocked(this.auth.isSuperAdmin, this.billingSettings)
    ) {
      this.domainRulesCheckbox.checked = false;
      this.domainRulesEnabled = false;
      const planName = this.billingSettings?.planName?.toLowerCase() || "basic";
      this.dialog.open(LimitPlanDialogComponent, {
        width: "500px",
        maxWidth: "90vw",
        data: {
          plan: planName as "free" | "freemium" | "basic",
          type: "domainRules",
        },
      });
      return;
    }
    this.domainRulesEnabled = checked;
    this.checkboxChanged = true;
  }

  toggleLockSignatureEditing(checked: boolean): void {
    this.lockSignatureEditingEnabled = checked;
    this.checkboxChanged = true;
  }

  saveExtensionSettings(): void {
    if (!this.currentSettings) {
      this.toastr.error(
        this.translate.instant("extension.loadError") as string,
      );
      return;
    }

    if (this.currentSettings.domainRulesEnabled && !this.domainRulesEnabled) {
      this.showDisableDomainRulesConfirmation();
      return;
    }

    this.performSave();
  }

  private showDisableDomainRulesConfirmation(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        body: this.translate.instant(
          "extension.disableDomainRulesConfirmationBody",
        ) as string,
        confirmButton: this.translate.instant("yes") as string,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.performSave();
      } else {
        this.domainRulesEnabled =
          this.currentSettings?.domainRulesEnabled === true;
        this.checkboxChanged = false;
      }
    });
  }

  private performSave(): void {
    const currentSettings = this.currentSettings;
    if (!currentSettings) {
      return;
    }

    this.saving = true;

    const { proxy: _, ...settingsWithoutProxy } = currentSettings;
    const updatedSettings: IntegrationSettings = {
      ...settingsWithoutProxy,
      internalSignature: this.internalSignatureEnabled,
      domainRulesEnabled: this.domainRulesEnabled,
      lockSignatureEditing: this.lockSignatureEditingEnabled,
    };

    this.store.dispatch(new ChangeIntegrationSettingsAction(updatedSettings));
  }
}
